import { apiFailureToErrorCode } from '@pomofocus/core';
import axios from 'axios';
let apiClient;
export const initializeApiClient = (baseURL, tokenProvider, clientVersion) => {
    if (!baseURL) {
        throw new Error('API baseURL is required');
    }
    if (apiClient !== undefined) {
        console.warn('apiClient already initialized');
        return;
    }
    apiClient = axios.create({
        baseURL,
        timeout: 10000,
        headers: {
            'Content-Type': 'application/json',
            ...(clientVersion && { 'X-Client-Version': clientVersion }),
        },
    });
    // Request interceptor to add auth token and version
    apiClient.interceptors.request.use((config) => {
        // Add auth token if available
        if (tokenProvider) {
            const token = tokenProvider.getToken();
            if (token) {
                config.headers.Authorization = token;
            }
        }
        // Add version header if not already set
        if (clientVersion && !config.headers['X-Client-Version']) {
            config.headers['X-Client-Version'] = clientVersion;
        }
        return config;
    }, (error) => {
        return Promise.reject(error);
    });
    // Response interceptor for error handling
    apiClient.interceptors.response.use((response) => response, (error) => {
        if (error.response && error.response.status === 426) {
            // Version mismatch - upgrade required
            const win = globalThis;
            if (win.window) {
                win.window.alert('A new version is available. The page will now reload to update.');
                win.window.location.reload();
            }
        }
        return Promise.reject(error);
    });
};
const isNetworkError = (error) => !error?.response && !!error?.request;
const MAX_NETWORK_RETRIES = 2;
export const getApiClient = () => {
    if (!apiClient) {
        throw new Error('apiClient not initialized. Call initializeApiClient() first.');
    }
    const executeRequest = async ({ url, method, data, headers, }) => {
        if (method === 'get') {
            return apiClient({ url, method: 'get', params: data, headers });
        }
        if (method === 'post') {
            return apiClient({ url, method: 'post', data, headers });
        }
        throw new Error(`Unsupported method: ${method}`);
    };
    const _apiClient = async (request) => {
        const maxRetries = request.retryOnNetworkError
            ? MAX_NETWORK_RETRIES
            : 0;
        let lastError;
        let attempt = 0;
        while (attempt <= maxRetries) {
            try {
                // eslint-disable-next-line no-await-in-loop
                return (await executeRequest(request));
            }
            catch (error) {
                lastError = error;
                if (isNetworkError(error) && attempt < maxRetries) {
                    console.warn(`Network error on ${request.method} ` +
                        `${request.url}, retrying ` +
                        `(${attempt + 1}/${maxRetries})`);
                    attempt += 1;
                }
                else {
                    break;
                }
            }
        }
        const formattedError = formatApiError(lastError, {
            url: request.url,
            attempts: attempt + 1,
        });
        throw formattedError;
    };
    return _apiClient;
};
const formatNetworkErrorMessage = (error, context) => {
    // error.code: axios error code (e.g. ERR_NETWORK, ECONNABORTED, ECONNRESET)
    // error.message: human-readable (e.g. "Network Error", "timeout of 10000ms exceeded")
    const parts = [];
    parts.push(error?.message || 'No response received');
    if (error?.code) {
        parts.push(`[${error.code}]`);
    }
    if (context.url) {
        parts.push(`url: ${context.url}`);
    }
    if (context.attempts && context.attempts > 1) {
        parts.push(`attempts: ${context.attempts}`);
    }
    if (typeof navigator !== 'undefined' && 'onLine' in navigator) {
        parts.push(`online: ${navigator.onLine}`);
    }
    return parts.join(' | ');
};
const formatApiError = (error, context = {}) => {
    if (error?.response?.status) {
        const status = error.response.status;
        return {
            statusCode: status,
            errorCode: apiFailureToErrorCode('http', status),
            message: error.response.data?.message ||
                error.message ||
                'Request failed',
        };
    }
    if (error?.request) {
        return {
            statusCode: 0,
            errorCode: apiFailureToErrorCode('network', 0),
            message: formatNetworkErrorMessage(error, context),
        };
    }
    return {
        statusCode: 0,
        errorCode: apiFailureToErrorCode('unknown', 0),
        message: error?.message || 'Unknown error occurred',
    };
};
